import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Text } from 'react-native';

import { useTheme } from '@/components/theme-provider';
import { typography } from '@/styles';
import { mailingBlockLines, type MailingAddressFields } from '@/utils/postal-address';

/**
 * The unit address with an off-site mailing address folded in underneath it.
 *
 * A cell that opts out of DataTable's plain-string path has to re-state the
 * table's cell typography by hand. Every other Address cell is a bare string
 * styled by RDT's `cells` style — 12px, `typography.fontFamily`,
 * `theme.colors.onSurface`. A React Native `<Text>` inside a custom `cell`
 * inherits none of that (react-native-web gives it 14px / black / system font),
 * so the one row with a mailing address would otherwise render visibly bigger
 * and darker than its neighbours.
 *
 * The root is a `<Text>`, never a `<View>`: the native `DataTable` wraps every
 * cell in a `<Text>`, where a nested View only lays out as an inline attachment. The
 * mailing line follows a newline inside that one Text, which breaks the line on
 * web (react-native-web Text is `pre-wrap`) and native alike.
 */
export const AccountAddressCell: React.FC<{ address?: string | null; mailingLines: string[] }> = ({
	address, mailingLines,
}) => {
	const theme = useTheme();
	const text = { fontSize: 12, fontFamily: typography.fontFamily, color: theme.colors.onSurface };
	return (
		<Text style={text}>
			{address ?? ''}
			{'\n'}
			{/* Reads as another address line — same type, comma-joined like every other
			    address in this column — with the envelope glyph as the only marker that
			    this one is where the mail goes. Wrapping comes from the column's `wrap`. */}
			<Ionicons name="mail-outline" size={12} color="#78909c" />: {mailingLines.join(', ')}
		</Text>
	);
};

/**
 * Address-column `cell` for any table of accounts whose search box matches the
 * mailing address (#549, #550). A surface that matches on a field it never shows
 * returns rows containing none of the words typed, which reads as a bug.
 *
 * With nothing worth showing (`mailingBlockLines` → `[]`) it hands back the plain
 * string, so the table styles it exactly as before. `empty` is what that string is
 * when the account has no unit address at all.
 */
export const accountAddressCell = (row: MailingAddressFields, empty = '—'): React.ReactNode => {
	const lines = mailingBlockLines(row);
	if (lines.length === 0) return row.address ?? empty;
	return <AccountAddressCell address={row.address} mailingLines={lines} />;
};
